/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import type { JSX } from "react"
import { useEffect, useMemo, useRef, useState } from "react"

import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { SessionViewModel } from "../../features/session/session-view-model"
import { useKeyboardShortcut } from "../../utils/use-keyboard-shortcut"
import { useViewModel } from "../app/app-hooks"
import { SidebarOffcanvasItemView } from "./sidebar-offcanvas-item-view"

import "./sidebar-offcanvas-view.css"
import "./sidebar-view.css"

// Now very specific component tied to Application selection.
// If necessary, refactor to be more generic.

interface Props {
    isOpened: boolean
    onClose: () => void
}

export const SidebarOffcanvasView = observer(({ isOpened, onClose }: Props): JSX.Element => {
    const vm = useViewModel(ApplicationTypeViewModel)
    const sessionVM = useViewModel(SessionViewModel)
    const favApps = useMemo(() => sessionVM.favApps, [sessionVM.favApps])
    const animationListener = useRef(null)
    const [isAnimating, setIsAnimating] = useState(false)

    useEffect(() => {
        const offcanvas = document.getElementById("sidebarOffcanvas")
        if (!offcanvas || animationListener.current === null) return

        const handleAnimationStart = () => {
            setIsAnimating(true)
        }

        const handleAnimationEnd = (event: AnimationEvent) => {
            setIsAnimating(false)

            if (event.animationName === "sidebarOpen") {
                offcanvas?.classList.add("opened")
                offcanvas?.classList.remove("closed")
                offcanvas?.classList.remove("animating-open")
            }
            if (event.animationName === "sidebarClose") {
                offcanvas?.classList.add("closed")
                offcanvas?.classList.remove("opened")
                offcanvas?.classList.remove("animating-close")
            }
        }

        offcanvas.addEventListener("animationstart", handleAnimationStart)
        offcanvas.addEventListener("animationend", handleAnimationEnd)

        return () => {
            offcanvas.removeEventListener("animationstart", handleAnimationStart)
            offcanvas.removeEventListener("animationend", handleAnimationEnd)
            animationListener.current = null
        }
    }, [])

    // Sidebar Offcanvas animation logic
    // `isAnimating` is read as a guard, not depended on: this should fire when
    // the sidebar is asked to open or close. Listing it would re-run the effect
    // when the animation *finishes* and isAnimating flips back to false, which
    // re-adds the class and restarts the animation.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see above
    useEffect(() => {
        const offcanvas = document.getElementById("sidebarOffcanvas")
        if (isAnimating) return // do nothing, wait for animation to finish

        // do not run animation on an already closed side menu
        if (offcanvas?.classList.contains("closed") && !isOpened) return

        if (isOpened) {
            offcanvas?.classList.add("animating-open")
        } else {
            offcanvas?.classList.add("animating-close")
        }
    }, [isOpened])

    // Sidebar Backdrop animation logic
    // biome-ignore lint/correctness/useExhaustiveDependencies: same guard as above — isAnimating must not re-trigger this
    useEffect(() => {
        const offcanvas = document.getElementById("sidebarOffcanvas")
        const backdrop = document.getElementById("sidebarBackdrop")
        if (isAnimating) return // do nothing, wait for animation to finish

        // do not run animation on an already closed side menu
        if (offcanvas?.classList.contains("closed") && !isOpened) return

        if (isOpened) {
            backdrop?.classList.add("show")
        } else {
            backdrop?.classList.remove("show")
        }
    }, [isOpened])

    // Preserve default Escape handling, such as aborting an in-progress navigation.
    useKeyboardShortcut("Escape", onClose, { preventDefault: false })

    return (
        <>
            <div
                id="sidebarOffcanvas"
                ref={animationListener}
                className="sidebar-offcanvas-view closed rounded-legacy-lg shadow-legacy-lg"
                tabIndex={-1}
            >
                <div className="offcanvas-header">
                    <img src="/assets/ic-window.svg" alt="Applications" width="16" className="me-2 mb-2" />
                    <h5 className="offcanvas-title mb-2">Applications</h5>
                </div>
                <div className="offcanvas-body">
                    <div>
                        <span className="dot enabled me-2"></span>
                        <p className="text-legacy-small text-legacy-muted inline-block">
                            Enabled apps are visible in the Dashboard.
                        </p>
                    </div>

                    <div className="flex items-center">
                        <img
                            src="/assets/ic-star-fill.svg"
                            alt="Favourite Applications"
                            width="16"
                            className="me-2 mb-2"
                        />
                        <h5 className="mb-2">Favourite Applications</h5>
                    </div>

                    {favApps.length === 0 && (
                        <ul className="list-group list-none pl-0 mb-6">
                            <li className="list-group-item flex text-legacy-muted">
                                <p className="my-auto">No favourite applications found.</p>
                            </li>
                        </ul>
                    )}
                    {favApps.length > 0 && (
                        <ul className="list-group list-none pl-0 mb-6">
                            {vm.filterFavApplicationTypes(sessionVM.favApps).map((appType) => (
                                <SidebarOffcanvasItemView
                                    key={appType.id ?? 0}
                                    appType={appType}
                                    isFavorite={sessionVM.isFavApp(appType.id ?? null)}
                                    onFavoriteChange={() => sessionVM.toggleFavApp(appType.id ?? null)}
                                    isEnabled={sessionVM.isEnabledApp(appType.id ?? null)}
                                    onEnabledChange={() => sessionVM.toggleEnabledApp(appType.id ?? null)}
                                />
                            ))}
                        </ul>
                    )}

                    <div className="flex items-center">
                        <img
                            src="/assets/ic-window-stack.svg"
                            alt="Favourite Applications"
                            width="16"
                            className="me-2 mb-2"
                        />
                        <h5 className="mb-2">All Applications</h5>
                    </div>

                    {!vm.hasUserAppTypes && (
                        <ul className="list-group list-none pl-0 mb-6">
                            <li className="p-legacy text-legacy-muted list-group-item">No applications found.</li>
                        </ul>
                    )}
                    {vm.hasUserAppTypes && (
                        <ul className="list-group list-none pl-0">
                            {vm.userAppTypes.map((appType) => (
                                <SidebarOffcanvasItemView
                                    key={appType.id ?? 0}
                                    appType={appType}
                                    isFavorite={sessionVM.isFavApp(appType.id ?? null)}
                                    onFavoriteChange={() => sessionVM.toggleFavApp(appType.id ?? null)}
                                    isEnabled={sessionVM.isEnabledApp(appType.id ?? null)}
                                    onEnabledChange={() => sessionVM.toggleEnabledApp(appType.id ?? null)}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            </div>
            <div id="sidebarBackdrop" className="sidebar-backdrop" onClick={() => onClose()}></div>
        </>
    )
})
